import { useEffect, useState } from "react";
import { Eye, Loader2, RefreshCw } from "lucide-react";
import type { FilterGroup, PartnerGoal } from "@colab/shared";
import { Button } from "@/components/ui/button";
import { usePreviewGoal, type GoalPreviewResult } from "./use-preview-goal";
import { defaultPeriodFor } from "./goals-period";

// SPA port of /app/admin/goals/[id]/_components/GoalPreviewPane.tsx.

export function GoalPreviewPane({
  goal,
  seed,
}: {
  goal: PartnerGoal;
  seed: number;
}) {
  const preview = usePreviewGoal();
  const [result, setResult] = useState<GoalPreviewResult | null>(null);
  const fallback = defaultPeriodFor(goal.frequency) ?? {
    start: new Date().toISOString().slice(0, 10),
    end: new Date().toISOString().slice(0, 10),
  };
  const [periodStart, setPeriodStart] = useState(fallback.start);
  const [periodEnd, setPeriodEnd] = useState(fallback.end);

  function runPreview() {
    preview.mutate(
      {
        spec: {
          data_source: goal.data_source,
          aggregation_kind: goal.aggregation_kind,
          aggregation_property: goal.aggregation_property,
          date_property: goal.date_property,
          attribution: goal.attribution,
          filter: goal.filter as FilterGroup,
        },
        partnerId: goal.partner_id,
        period: { start: periodStart, end: periodEnd },
      },
      {
        onSuccess: (data) => setResult(data.result),
        onError: () => setResult({ count: 0, sum: 0, matchedSample: [] }),
      },
    );
  }

  useEffect(() => {
    runPreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seed]);

  const headlineValue =
    result === null
      ? "—"
      : goal.aggregation_kind === "sum"
        ? formatNumber(result.sum)
        : formatNumber(result.count);

  return (
    <div className="space-y-3 rounded-xl border bg-card p-5">
      <div className="flex items-center justify-between">
        <h2 className="inline-flex items-center gap-2 font-display text-sm font-semibold tracking-tight">
          <Eye className="h-4 w-4" /> Live preview
        </h2>
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={runPreview}
          disabled={preview.isPending}
        >
          {preview.isPending ? (
            <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
          ) : (
            <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
          )}
          Refresh
        </Button>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div className="space-y-1">
          <label className="text-[11px] uppercase tracking-wider text-muted-foreground">
            From
          </label>
          <input
            type="date"
            value={periodStart}
            onChange={(e) => setPeriodStart(e.target.value)}
            onBlur={runPreview}
            className="h-8 w-full rounded-md border bg-background px-2 text-xs"
          />
        </div>
        <div className="space-y-1">
          <label className="text-[11px] uppercase tracking-wider text-muted-foreground">
            To
          </label>
          <input
            type="date"
            value={periodEnd}
            onChange={(e) => setPeriodEnd(e.target.value)}
            onBlur={runPreview}
            className="h-8 w-full rounded-md border bg-background px-2 text-xs"
          />
        </div>
      </div>

      <div className="rounded-lg bg-muted/30 p-4 text-center">
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
          {goal.aggregation_kind === "sum"
            ? `Sum of ${goal.aggregation_property ?? "—"}`
            : "Count of rows"}
        </div>
        <div className="mt-1 font-display text-3xl font-semibold tabular-nums">
          {headlineValue}
        </div>
        {result && goal.aggregation_kind === "sum" && (
          <div className="mt-0.5 text-[11px] text-muted-foreground tabular-nums">
            across {formatNumber(result.count)} rows
          </div>
        )}
      </div>

      {result && result.matchedSample.length > 0 && (
        <div>
          <div className="mb-1.5 text-[10px] uppercase tracking-wider text-muted-foreground">
            Sample (top {result.matchedSample.length})
          </div>
          <ul className="space-y-1">
            {result.matchedSample.map((s) => (
              <li
                key={s.id}
                className="flex items-center justify-between gap-2 text-xs"
              >
                <span className="truncate">{s.label}</span>
                <span className="shrink-0 tabular-nums text-muted-foreground">
                  {s.value != null
                    ? formatNumber(s.value)
                    : s.date
                      ? s.date.slice(0, 10)
                      : ""}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function formatNumber(n: number): string {
  if (Number.isInteger(n)) return n.toLocaleString();
  return n.toLocaleString(undefined, { maximumFractionDigits: 2 });
}
